import { FC, useEffect, useState } from "react";
import { Box, ClickAwayListener, Collapse, ListItemButton, MenuItem, MenuList, Popover } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { v4 as uuid } from "uuid";
import { AppMenuSection, isRouteActive } from "../../../../../../../../common/routesUtils";
import { PageType } from "../../../../../../../../config/page.types";
import { useBreadcrumbs } from "../../../../../../../../context/BreadcrumbsProvider";
import { usePermissions } from "../../../../../../../../permissions/usePermissions";
import SectionItem from "../SectionItem";

interface Props {
	menuIsOpen: boolean;
	item: AppMenuSection;
}

const MultipleRoutes: FC<Props> = ({ menuIsOpen, item }) => {
	const navigateTo = useNavigate();
	const { t } = useTranslation("navigation");
	const { breadcrumbs, visitPage } = useBreadcrumbs();
	const [showRoutes, setShowRoutes] = useState<boolean>(false); // expand/collapse state
	const [anchor, setAnchor] = useState<HTMLDivElement | null>(null); // popover anchor

	const id = showRoutes ? uuid() : undefined; // unique popover id

	const { pathname } = useLocation();
	const { canAccessPage } = usePermissions();

	// a sub-item is enabled if the user can access its page; the section is disabled if no sub-item is enabled
	const enabledRoutes = new Set(item.items.filter((i) => canAccessPage(i.pageType)).map((i) => i.route));
	const isSectionDisabled = enabledRoutes.size === 0;

	// determine if parent or any child is selected: by current route first (covers nested pages,
	// e.g. /results/transport/greenrouter), then by breadcrumbs
	const lastPageType = breadcrumbs.length > 0 ? breadcrumbs[breadcrumbs.length - 1].pageType : null;
	const childByRoute = item.items.find((i) => isRouteActive(i.route, pathname));
	const childByBreadcrumbs = [...breadcrumbs]
		.reverse()
		.map((b) => item.items.find((i) => i.pageType === b.pageType))
		.find((i) => i !== undefined);
	const isSelected =
		(item.route !== undefined && isRouteActive(item.route, pathname)) ||
		childByRoute !== undefined ||
		lastPageType === item.pageType ||
		item.items.some((i) => i.pageType === lastPageType);
	const selectedChildRoute = isSelected ? (childByRoute ?? childByBreadcrumbs)?.route : undefined;

	// toggle routes menu open/close
	const handleRoutesMenu = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
		if (menuIsOpen) {
			setShowRoutes(!showRoutes);
		} else {
			setShowRoutes(true);
			setAnchor(e.currentTarget); // set anchor for popover when menu is closed
		}
	};

	// close popover/menu
	const closeMenu = () => {
		setShowRoutes(false);
		setAnchor(null);
	};

	// handle click on a child route
	const itemClicked = (route: string, pageType: string) => {
		if (!menuIsOpen) closeMenu(); // close popover if menu collapsed
		visitPage(pageType as PageType, true); // update breadcrumb (from menu)
		navigateTo(route); // navigate to route
	};

	// update menu open/collapse state when menu toggles or selection changes
	useEffect(() => {
		setAnchor(null);
		if (!menuIsOpen) {
			closeMenu();
		} else {
			setShowRoutes(isSelected);
		}
	}, [menuIsOpen, isSelected]);

	return (
		<>
			{/* main section item */}
			<SectionItem
				expand={showRoutes ? "less" : "more"} // toggle icon
				itemKey={item.key}
				disabled={isSectionDisabled}
				state={isSelected ? (menuIsOpen && showRoutes ? "ancestor" : "active") : undefined} // when expanded, the selected child gets the pill
				menuIsOpen={menuIsOpen}
				onClick={handleRoutesMenu} // open/collapse
			/>

			{/* expanded routes when menu is open */}
			{menuIsOpen && (
				<Collapse in={showRoutes} timeout="auto" unmountOnExit>
					{/* vertical guide line aligned with the section icon */}
					<Box sx={{ ml: "27px", mb: 1, borderLeft: 1, borderColor: "divider" }}>
						{item.items.map((i) => {
							const childSelected = i.route === selectedChildRoute;
							const childDisabled = !enabledRoutes.has(i.route);
							return (
								<ListItemButton
									selected={childSelected}
									disabled={childDisabled}
									onClick={() => itemClicked(i.route, i.pageType)}
									key={i.route}
									sx={{
										ml: 1,
										mr: 1,
										my: 0.25,
										pl: "11px", // aligns child labels with the section label
										py: 0.75,
										borderRadius: 2,
										"&.Mui-selected, &.Mui-selected:hover": {
											bgcolor: "action.selected",
											color: "primary.main",
										},
									}}
								>
									{t([`breadcrumbs/${i.key}`, `breadcrumbs/<${i.key}>`])}
								</ListItemButton>
							);
						})}
					</Box>
				</Collapse>
			)}

			{/* popover when menu is collapsed */}
			{!menuIsOpen && (
				<Popover
					id={id}
					open={showRoutes}
					anchorEl={anchor}
					anchorOrigin={{ vertical: "center", horizontal: "right" }}
				>
					<ClickAwayListener onClickAway={closeMenu}>
						<MenuList autoFocusItem>
							{item.items.map((i) => {
								const childSelected = i.route === selectedChildRoute;
								const childDisabled = !enabledRoutes.has(i.route);
								return (
									<MenuItem
										selected={childSelected}
										disabled={childDisabled}
										key={i.route}
										onClick={() => itemClicked(i.route, i.pageType)}
									>
										{t([`breadcrumbs/${i.key}`, `breadcrumbs/<${i.key}>`])}
									</MenuItem>
								);
							})}
						</MenuList>
					</ClickAwayListener>
				</Popover>
			)}
		</>
	);
};

export default MultipleRoutes;
